import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import WebIcon from '@mui/icons-material/Web';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';
import { getMatch, getAnnouncements, postAnnouncement, deleteAnnouncement } from '../../api';
import Snackbar from '../../components/Snackbar';
import { wrappableHtml } from '../../utils/html';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

function AnnouncementEditor({ value, onChange }) {
  return (
    <ReactQuill
      theme="snow"
      value={value}
      onChange={onChange}
      style={{ height: 200 }}
      modules={QUILL_MODULES}
    />
  );
}

export default function Announcements() {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const [match, setMatch] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // New announcement state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [postToWebsite, setPostToWebsite] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [matchRes, annRes] = await Promise.all([
        getMatch(matchId),
        getAnnouncements(matchId).catch(() => ({ data: [] })),
      ]);
      setMatch(matchRes.data);
      setAnnouncements(Array.isArray(annRes.data) ? annRes.data : []);
    } catch (err) {
      setSnack({ open: true, message: t('announcements.loadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!subject.trim()) {
      setSnack({ open: true, message: t('announcements.subjectRequired'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const announceBy = postToWebsite && sendEmail ? 'website+email'
        : sendEmail ? 'email'
        : 'website';
      await postAnnouncement(matchId, {
        announceBy,
        announceByEmailTo: emailTo,
        announcement: {
          subject: subject.trim(),
          body: body.trim(),
        },
      });
      await loadData();
      setDialogOpen(false);
      setSubject('');
      setBody('');
      setPostToWebsite(true);
      setSendEmail(false);
      setEmailTo('all');
      setSnack({ open: true, message: t('announcements.posted'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('announcements.postFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (announcementId) => {
    try {
      await deleteAnnouncement(matchId, announcementId);
      setAnnouncements((prev) => prev.filter((a) => a.id !== announcementId));
      setSnack({ open: true, message: t('announcements.deleted', 'Announcement deleted'), severity: 'success' });
    } catch {
      setSnack({ open: true, message: t('common.saveError'), severity: 'error' });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('matches.announcements')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          {t('announcements.newAnnouncement')}
        </Button>
      </Box>

      {announcements.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">{t('announcements.noAnnouncements')}</Typography>
        </Box>
      )}

      {[...announcements].sort((a, b) => (b.created || 0) - (a.created || 0)).map((ann, idx) => (
        <Card key={ann.id || idx} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h6">{ann.subject || ann.announcement?.subject}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {ann.created ? new Date(ann.created).toLocaleString() : ''}
                </Typography>
              </Box>
              <Chip
                icon={ann.announceBy?.includes('email') ? <EmailIcon /> : <WebIcon />}
                label={ann.announceBy === 'website+email' ? t('announcements.websiteAndEmail') : ann.announceBy === 'email' ? t('announcements.email') : t('announcements.website')}
                size="small"
                variant="outlined"
              />
            </Box>
            <Box
              sx={{
                mt: 1,
                maxWidth: '100%',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                overflow: 'hidden',
                '& p': { m: 0 },
                '& img': { maxWidth: '100%' },
              }}
              dangerouslySetInnerHTML={{ __html: wrappableHtml(ann.body || ann.announcement?.body || '') }}
            />
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(ann.id)} sx={{ textTransform: 'none' }}>
              {t('common.delete')}
            </Button>
          </CardActions>
        </Card>
      ))}

      {/* Create Announcement Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('announcements.newAnnouncement')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('announcements.subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              required
            />
            <Typography variant="subtitle2">{t('announcements.body')}</Typography>
            <AnnouncementEditor value={body} onChange={setBody} />
            <Box sx={{ mb: 5 }} />
            <FormControlLabel
              control={<Checkbox checked={postToWebsite} onChange={(e) => setPostToWebsite(e.target.checked)} />}
              label={t('announcements.website')}
            />
            <FormControlLabel
              control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
              label={t('announcements.email')}
            />
            {sendEmail && (
              <FormControl fullWidth>
                <InputLabel>{t('announcements.emailTo')}</InputLabel>
                <Select value={emailTo} label={t('announcements.emailTo')} onChange={(e) => setEmailTo(e.target.value)}>
                  <MenuItem value="all">{t('announcements.emailAll')}</MenuItem>
                  <MenuItem value="paid">{t('announcements.emailPaid')}</MenuItem>
                  <MenuItem value="not-paid">{t('announcements.emailNotPaid')}</MenuItem>
                  {(match?.squads || []).map((sq) => (
                    <MenuItem key={sq.id} value={`squad-${sq.id}`}>{t('announcements.emailSquad', { squad: sq.title })}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving || !subject.trim() || (!postToWebsite && !sendEmail)}>
            {saving ? <CircularProgress size={20} /> : t('announcements.postAnnouncement')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('announcements.confirmDelete', 'Delete Announcement?')}</DialogTitle>
        <DialogContent>
          <Typography>{t('announcements.confirmDeleteMsg', 'Are you sure you want to delete this announcement? This cannot be undone.')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
